import { trigger, state, style, transition, animate } from '@angular/animations';
import { ChangeDetectorRef, Component, computed, ElementRef, EventEmitter, HostListener, Inject, Input, OnDestroy, OnInit, Output, Renderer2, Signal, signal, WritableSignal } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { TraceRouteData, TraceTTSettingsType, TraceWayPointType } from '../../../models/trace-route-data';
import { BrowserSnifferService } from '../../../services/browser-sniffer.service';
import { MapService } from '../../../services/map.service';
import { PopupService } from '../../../services/popup.service';
import { ClickedPoint, TraceDrawerType, TraceRouteDataUpdateType, TraceService } from '../../../services/trace.service';
import { FileDownloaderComponent, FileDownloaderSupportedFileTypes } from '../../file-downloader/file-downloader.component';
import { PdfService } from '../..//pdf/pdf.service';
import { TraceCreateConfirmationDialogComponent, TraceCreateConfirmationDialogData } from '../trace-create/trace-create-confirmation-dialog/trace-create-confirmation-dialog.component';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ShareMapModule } from '@tt-lib/components/share-map/share-map.module';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FileDownloaderModule } from '@tt-lib/components/file-downloader/file-downloader.module';
import { MatDividerModule } from '@angular/material/divider';
import { ShareMapComponent } from '@tt-lib/components/share-map/share-map.component';
import { BackEventService } from '@tt-lib/services/back-event.service';

@Component({
  selector: 'tt-trace-view',
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    // MatExpansionModule,
    MatDividerModule,
    MatTooltipModule,
    TranslateModule,
    ShareMapComponent,
    FileDownloaderModule
  ],
  templateUrl: './trace-view.component.html',
  styleUrls: ['../trace.scss'],
  standalone: true
})
export class TraceViewComponent implements OnInit, OnDestroy {
  private _mapElement: HTMLElement;
  private _olControlElements: NodeListOf<HTMLElement>;
  private _subscriptions: Subscription;
  public isActive: Signal<boolean> = computed(() => { return this.traceService.isInViewMode() });
  @Output() editEvent = new EventEmitter<boolean>();

  public ref_formattedRouteLength: WritableSignal<SafeHtml> = signal('0 <span>m</span>');
  public ref_formattedRouteUpwardDistance: WritableSignal<SafeHtml> = signal('_');
  public ref_formattedRouteDownwardDistance: WritableSignal<SafeHtml> = signal('_');
  public ref_hasWayPointsOnMap: WritableSignal<boolean> = signal(false);
  public ref_hasRouteOnMap: WritableSignal<boolean> = signal(false);
  public ref_isRouteWithGeneralInfo: WritableSignal<boolean> = signal(false);
  public ref_hasRouteToCreateProfile: WritableSignal<boolean> = signal(false);
  public ref_traceRouteData: WritableSignal<TraceRouteData> = signal(null);

  public ref_traceSettingsSequenceNumberAlpha: boolean = false;

  private _confirmationDialog: MatDialogRef<TraceCreateConfirmationDialogComponent, { data: TraceCreateConfirmationDialogData }>;

  //dynamic template attribute (setter/getter below)
  private _hideTraceCreate: boolean = false;

  constructor(
    private _elRef: ElementRef,
    private _mapService: MapService,
    private _renderer: Renderer2,
    private _popupService: PopupService,
    private _dialog: MatDialog,
    private _translateService: TranslateService,
    public traceService: TraceService,
    public browserSniffer: BrowserSnifferService,
    public pdfService: PdfService,
    private cdr: ChangeDetectorRef,
    protected sanitizer: DomSanitizer,
    private _backEventService: BackEventService
  ) {
    this._confirmationDialog = null;
    this._setReferencedInfo();

    this._subscriptions = new Subscription();

    this._subscriptions.add(
      this.traceService.updatedTraceRouteData$.subscribe((traceRouteDataUpdateType: TraceRouteDataUpdateType) => {
        //we use an COPY_TO_OTHER_TAB action in traceService.setInEditingMode
        if ((!this.isActive() && this.ref_traceRouteData() !== null) || (this.isActive() && (traceRouteDataUpdateType === TraceRouteDataUpdateType.COPY_TO_OTHER_TAB || traceRouteDataUpdateType === TraceRouteDataUpdateType.NEW_DATA || traceRouteDataUpdateType === TraceRouteDataUpdateType.UPDATED_ELEV_UPDOWN_DIST))) {
          this._setReferencedInfo();

          if (this.isActive() && traceRouteDataUpdateType === TraceRouteDataUpdateType.NEW_DATA) {
            window.setTimeout(() => {
              //temp timing calculation
              this._calculateRouteTime(true);
              this._calculateRouteTime(false);
            }, 0);
          }
        }
      })
    );

    this._subscriptions.add(
      this.traceService.clickedPoint$.subscribe((clickedPoint: ClickedPoint) => {
        if (this.isActive()) {
          const wasOpen = this.traceService.tabindex() === TraceDrawerType.VIEW_ROUTE && !this.traceService.isWatchingRouteWithoutDrawer();
          if (!wasOpen) {
            //if the import panel is open, this will switch the panel
            //if the drawer was closed completely, this will open it
            this.traceService.openTraceTab(TraceDrawerType.VIEW_ROUTE);
          }
          window.setTimeout(() => {
            this._scrollToInfo(clickedPoint);
          }, wasOpen ? 0 : 300);
        }
      })
    );

    this._subscriptions.add(
      this._popupService.customEvent.subscribe((eventObj) => {
        if (
          this.isActive() &&
          eventObj &&
          eventObj.detail &&
          eventObj.detail.eventId &&
          eventObj.detail.eventId === 'TraceInfoReadOn' &&
          eventObj.detail.data &&
          typeof eventObj.detail.data.isPoi === 'boolean' &&
          Array.isArray(eventObj.detail.data.ref) &&
          ((eventObj.detail.data.isPoi && eventObj.detail.data.ref.length === 1) || (!eventObj.detail.data.isPoi && eventObj.detail.data.ref.length === 2))
        ) {

          const pointObj: TraceWayPointType = eventObj.detail.data.isPoi ? this.traceService.getTraceRouteData_wayPoint(eventObj.detail.data.ref[0]) : this.traceService.getTraceRouteData_routePoint(eventObj.detail.data.ref[0], eventObj.detail.data.ref[1]);

          if (pointObj) {

            if (this._popupService.isPopupAlreadyOnMap()) {
              this._popupService.hide();
            }

            const wasOpen = this.traceService.tabindex() === TraceDrawerType.VIEW_ROUTE && !this.traceService.isWatchingRouteWithoutDrawer();
            if (!wasOpen) {
              //if the import panel is open, this will switch the panel
              //if the drawer was closed completely, this will open it
              this.traceService.openTraceTab(TraceDrawerType.VIEW_ROUTE);
            }

            window.setTimeout(() => {
              this.traceService.addWayPointHighlight(
                eventObj.detail.data.ref[0],
                true
              );

              //normally we don't have routePoint's in view mode, but we'll add it here in case we use it in the future
              this._scrollToInfo({ ref: eventObj.detail.data.isPoi ? 'wayPoint_' + eventObj.detail.data.ref[0] : 'routePoint_' + eventObj.detail.data.ref[0] + '_' + eventObj.detail.data.ref[1], isPoi: eventObj.detail.data.isPoi, isNew: false });
            }, wasOpen ? 0 : 300);
          }
        }
      })
    );
  }

  ngOnInit(): void {
    this._mapElement = this._mapService.getCurrentMap().getTargetElement();
    // this._olControlElements = document.querySelectorAll<HTMLElement>(
    //   'tt-zoom, .baselayer-selector-button-container, .legend-button-container, .remarks-select-button-container, .trace-select-button-container, .measure-button-container, .ol-scale-line, tt-mouse-position,  tt-mouse-position-mapindex'
    // ); 
    // mobile phone init viewcomponent
    this._setReferencedInfo();
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  @Input()
  set hideTraceCreate(val: string) {
    this._hideTraceCreate = val ? val.trim().toLowerCase() === "true" : false;
  }

  get hideTraceCreate(): boolean {
    return this._hideTraceCreate;
  }

  private _setReferencedInfo() {
    if (this.isActive()) {
      this.ref_hasWayPointsOnMap.set(this.traceService.hasWayPointsOnMap());
      this.ref_hasRouteOnMap.set(this.traceService.hasRouteOnMap());
      this.ref_hasRouteToCreateProfile.set(this.traceService.hasRouteToCreateProfile());
      this.ref_isRouteWithGeneralInfo.set(this.traceService.hasTraceRouteWithGeneralInfo());
      this.ref_traceRouteData.set(this.traceService.getTraceRouteData());
      this.ref_traceSettingsSequenceNumberAlpha = this.ref_traceRouteData().wayPoints.length < 27 && TraceRouteData.getTraceTTSetting(this.ref_traceRouteData().ttSettings, "wayPointSequenceNumber") === 'alpha';
    } else {
      this.ref_hasWayPointsOnMap.set(false);
      this.ref_hasRouteOnMap.set(false);
      this.ref_hasRouteToCreateProfile.set(false);
      this.ref_isRouteWithGeneralInfo.set(false);
      this.ref_traceRouteData.set(TraceRouteData.getEmptyTraceRouteDataObject(this._mapService.getCurrentMap().getView().getProjection().getCode()));
      this.ref_traceSettingsSequenceNumberAlpha = false;
    }
    this.ref_formattedRouteUpwardDistance.set(this.sanitizer.bypassSecurityTrustHtml(this.traceService.getElevationUpwardDist() > -1 ? TraceRouteData.formatRouteLength(this.traceService.getElevationUpwardDist(), true, '0 <span>m</span>') : '_'));
    this.ref_formattedRouteDownwardDistance.set(this.sanitizer.bypassSecurityTrustHtml(this.traceService.getElevationDownwardDist() < 1 ? '-&nbsp;' + TraceRouteData.formatRouteLength(Math.abs(this.traceService.getElevationDownwardDist()), true, '0 <span>m</span>') : '_'));

    this.ref_formattedRouteLength.set(this.isActive() ? TraceRouteData.formatRouteLength(this.traceService.getRouteLength(), true, '0 <span>m</span>') : '0 <span>m</span>');
    if (this.isActive() && this.ref_formattedRouteUpwardDistance() === '_') {
      window.setTimeout(() => {
        if (this.ref_formattedRouteUpwardDistance() === '_') {
          this.traceService.renewElevationProfileData(false);
        }
      }, 5000);
    }
  }

  /**
   * this handles wayPoint element highlight
   * adding listener to every wayPoint in html template (touchstart)='' creates many violation errors
   * @param event 
   */
  @HostListener('touchstart', ['$event'])
  //private _handleTouch(event: TouchEvent)
  protected handleTouch(event: TouchEvent) {
    let tag = <Element>event.target;
    while (!tag.classList.contains('wayPoint') && !tag.classList.contains('tab-viewport')) {
      tag = tag.parentElement;
    }
    if (tag.classList.contains('wayPoint')) {
      if (typeof tag.id === 'string' && tag.id.indexOf('_') > -1) {
        this.traceService.addWayPointHighlight(tag.id.indexOf('_startPoint') > -1 ? 'startPoint' : tag.id.indexOf('_endPoint') > -1 ? 'endPoint' : parseInt(tag.id.substring(tag.id.indexOf('_') + 1)), false);
      }
    }
  }

  private _calculateRouteTime(forHike: boolean) {
    if (this.traceService.getRouteLength() > 0) {
      let baseTime = 0;
      let elevationTime = 0;
      if (forHike) {
        //(12minPerKm * distKm) + (1min per 10m upwards elevation)
        baseTime = Math.ceil(12.0 * ((this.traceService.getRouteLength() * 1.0) / 1000.0));
        elevationTime = this.traceService.getElevationUpwardDist() > -1 ? Math.ceil((this.traceService.getElevationUpwardDist() * 1.0) / 10.0) : 0;
      } else {
        //average speed 16-20
        //every 100 meters of vertical elevation gain adds approximately 6 to 10 minutes to your total riding time, 
        //distKm/16kmh * 60
        baseTime = Math.ceil(60.0 * (((this.traceService.getRouteLength() * 1.0) / 1000.0) / 16.0));
        elevationTime = this.traceService.getElevationUpwardDist() > -1 ? Math.floor((this.traceService.getElevationUpwardDist() * 1.0) / 100.0) * 10 : 0;
      }
      console.log(`Timing ${forHike ? 'hiking trip' : 'biking trip'} : base ${baseTime}min , elevation ${elevationTime}min , total ${Math.floor((baseTime + elevationTime) / 60)}h${(baseTime + elevationTime) % 60} (distance ${this.traceService.getRouteLength()}m , upwards elevation ${this.traceService.getElevationUpwardDist()}m)`);
    }
  }

  public _scrollToInfo(clickedPoint: ClickedPoint) {
    //find better solutions to scroll to element ? but the container has different types of elements, not only wayPoint containers
    //!pushes element behind addressbar on phone, tried with other params (end,start) but no good
    const wayPointEl = this._elRef.nativeElement.querySelector( '#' + clickedPoint.ref);
    const scrollContainer = document.querySelector('.trace-data');
    const scrollPosition = wayPointEl?.getBoundingClientRect().top + scrollContainer?.scrollTop - scrollContainer.getBoundingClientRect().top;

    if (wayPointEl) {
      if (this.browserSniffer.isPhone()) {
        wayPointEl.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'center',
        });
      } else {
        scrollContainer.scrollTo({
          top: scrollPosition,
          behavior: 'smooth',
        });
      }

      if (!clickedPoint.isNew) {
        this._renderer.addClass(wayPointEl, 'clicked');

        window.setTimeout(() => {
          this._renderer.removeClass(wayPointEl, 'clicked');
        }, 1000);
      }
    }
  }

  openImport() {
    this.traceService.openTraceTab(TraceDrawerType.IMPORT_ROUTE);
  }

  downloadTraceData() {
    //the fileName will be checked by the file-downloader component and possibly rejected
    this._dialog.open(FileDownloaderComponent, {
      panelClass: 'medium-dialog',
      data: {
        vectorLayerIds: [this.traceService.getDrawLayerId('point'), this.traceService.getDrawLayerId('line')],
        title: this._translateService.instant('trace.download-route-popuptitle'),
        selectedFileType: FileDownloaderSupportedFileTypes.GPX,
        fileName: this.traceService.getTraceRouteData().metadata.name,
        metadata: this.traceService.getTraceRouteData().metadata
        /*
        no file type selection?
        selectedFileTypeOnly: true,
        */
        /*
        description: "extra text above input fields"
        */
      }
    });
    this._backEventService.registerOpenedWindow({ windowName: 'FileDownloaderComponent', isAutoCloseableWindow: true });
  }

  editRoute() {
    if (sessionStorage.getItem('drawdisclaimer') !== 'shown') {
      this.traceService.getDrawDisclaimerDialog().afterClosed().subscribe(() => {
        sessionStorage.setItem('drawdisclaimer', 'shown');
        //case use was dragging window to phone size when disclaimer was open
        (!this.browserSniffer.isPhone()) ? this.traceService.openTraceTab(TraceDrawerType.CREATE_ROUTE) : this.traceService.onOpenCreateMobile();
      })
    }
    else {
      this.traceService.openTraceTab(TraceDrawerType.CREATE_ROUTE);
    }
  }
  editRoutePhone() {
    this.traceService.closeViewDialog();
    this.traceService.openCreateMobile(true);
  }

  resetData() {
    if (this._confirmationDialog !== null) {
      this._confirmationDialog.close();
      this._confirmationDialog = null;
    }

    this._confirmationDialog = this._dialog.open(TraceCreateConfirmationDialogComponent, {
      panelClass: 'small-dialog',
      data: {
        title: this._translateService.instant('trace.create-route.reset'),
        text: this._translateService.instant('trace.create-route.confirmdelete-all'),
        type: 'resetData'
      },
    });

    this._confirmationDialog.afterClosed().pipe(take(1)).subscribe(result => {
      if (result && result.data && result.data.type === 'resetData') {
        if (this.browserSniffer.isPhone()) {
          this.traceService.closeViewDialog();
        }
        this.traceService.setPermalinkParam(false);
        this.traceService.fullCleanupAndSaveForHistoryItems();
        this.traceService.addNewHistoryItemForEditing();
        this.traceService.setPermalinkParam(true);

        this.traceService.resetTraceRouteData(true);
      }
      this._confirmationDialog = null;
    });

    this._backEventService.registerOpenedWindow({ windowName: 'TraceCreateConfirmationDialogComponent', isAutoCloseableWindow: true });
  }

  convertWayPointIndexToLetter(numb: number, uppercase = true) {
    return TraceRouteData.convertWayPointIndexToLetter(numb, uppercase);
  }
}
